import { memo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, CheckCircle2, Copy, Globe, GripVertical, Hourglass, Lightbulb, MousePointerClick, Trash2 } from '@/components/icons';
import { STEP_KINDS, canBeWrittenValue, isBuiltin, placeholderText, type Platform, type StepKind, type TestStep } from '@1qa/shared';
import { fieldBase } from '@/components/ui';
import { insertAt } from '@/lib/testData';
import { KIND_META } from './describe';
import { InsertVariableMenu, type InsertableVariable } from './InsertVariableMenu';
import { stepProblems, type BuilderAction } from './stepReducer';

export const KIND_ICONS: Record<StepKind, typeof Globe> = {
  open: Globe,
  act: MousePointerClick,
  assert: CheckCircle2,
  wait: Hourglass,
};

export const KIND_TONES: Record<StepKind, string> = {
  open: 'bg-info-subtle text-link ring-link/40',
  act: 'bg-primary-subtle text-primary ring-primary/40',
  assert: 'bg-success-subtle text-success ring-success/40',
  wait: 'bg-warning-subtle text-warning ring-warning/40',
};

interface Props {
  step: TestStep;
  index: number;
  total: number;
  platform: Platform;
  readOnly: boolean;
  showErrors: boolean;
  dispatch: (a: BuilderAction) => void;
  dragging: boolean;
  dropTarget: 'before' | 'after' | null;
  onDragStart: (index: number) => void;
  onDragOver: (index: number, e: DragEvent) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
  /** Saved test data that can be inserted as {{KEY}}. */
  variables: InsertableVariable[];
  /** Test-data problems for this step (unknown name, secret in the wrong step kind). Always shown. */
  variableMessages?: string[];
}

export const StepCard = memo(function StepCard({
  step,
  index,
  total,
  platform,
  readOnly,
  showErrors,
  dispatch,
  dragging,
  dropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  variables,
  variableMessages,
}: Props) {
  const [showHelp, setShowHelp] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const meta = KIND_META[step.kind];
  const problem = stepProblems(step);
  const inputId = `step-${step.id}`;
  const mobileOpen = step.kind === 'open' && platform !== 'WEB';
  const canInsert = !readOnly && !mobileOpen && step.kind !== 'wait';

  const insertVariable = (name: string) => {
    const el = textRef.current;
    const start = el && document.activeElement === el ? el.selectionStart : (el?.selectionStart ?? step.value.length);
    const end = el ? el.selectionEnd : step.value.length;
    const from = start ?? step.value.length;
    const to = end ?? step.value.length;
    // Replacing selected text keeps it as the written value ({{NAME|text}}), used when the test data
    // has no NAME. Never for secrets (the value would be readable in the test) or built-ins.
    const selected = step.value.slice(from, to);
    const secret = variables.find((v) => v.key === name)?.isSecret;
    const keep = !secret && !isBuiltin(name) && canBeWrittenValue(selected);
    const next = insertAt(step.value, from, to, placeholderText(name, keep ? selected : undefined));
    dispatch({ type: 'update', id: step.id, patch: { value: next.value } });
    requestAnimationFrame(() => {
      const t = textRef.current;
      if (!t) return;
      t.focus();
      t.setSelectionRange(next.cursor, next.cursor);
    });
  };
  const variableErrId = `${inputId}-vars`;

  return (
    <li
      className={clsx('relative', dragging && 'opacity-40')}
      onDragOver={(e) => onDragOver(index, e)}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
    >
      {dropTarget === 'before' && <div className="absolute -top-2 right-0 left-0 h-0.5 rounded-full bg-primary" aria-hidden />}
      <div className="group flex gap-3 rounded-card bg-surface p-4 ring-1 ring-border ring-inset transition hover:ring-fg-muted focus-within:ring-primary">
        <div className="flex flex-col items-center gap-2">
          <span className="grid size-8 place-items-center rounded-full bg-surface-sunken font-display text-xs tracking-label text-fg-secondary ring-1 ring-border ring-inset">
            {index + 1}
          </span>
          {!readOnly && (
            <span
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(index));
                onDragStart(index);
              }}
              onDragEnd={onDragEnd}
              className="cursor-grab rounded-bar p-0.5 text-fg-muted hover:text-fg-secondary active:cursor-grabbing"
              title="Drag to reorder"
              aria-hidden
            >
              <GripVertical className="size-5" />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label={`Step ${index + 1} type`}>
            {STEP_KINDS.map((k) => {
              const Icon = KIND_ICONS[k];
              const active = step.kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={readOnly}
                  onClick={() => dispatch({ type: 'update', id: step.id, patch: { kind: k } })}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[11px]/[16.5px] font-semibold tracking-label uppercase ring-1 ring-inset transition',
                    active ? KIND_TONES[k] : 'text-fg-secondary ring-transparent hover:bg-fg/8 hover:text-fg',
                    readOnly && !active && 'hidden',
                  )}
                >
                  <Icon className="size-3.5" />
                  {KIND_META[k].label}
                </button>
              );
            })}
            {!readOnly && (
              <div className="ml-auto flex items-center">
                {canInsert && <InsertVariableMenu
                    variables={variables}
                    allowSecrets={step.kind === 'act'}
                    onInsert={insertVariable}
                    selectedText={() => {
                      const el = textRef.current;
                      return el ? step.value.slice(el.selectionStart, el.selectionEnd) : '';
                    }}
                  />}
                <button
                  type="button"
                  onClick={() => setShowHelp((s) => !s)}
                  aria-expanded={showHelp}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-fg-muted hover:text-primary"
                >
                  <Lightbulb className="size-3.5" /> Examples
                </button>
              </div>
            )}
          </div>

          <label htmlFor={inputId} className="sr-only">
            Step {index + 1}: {meta.label}
          </label>
          {mobileOpen ? (
            <p className="rounded-field bg-surface-sunken px-4 py-3 text-sm text-fg-muted">Launches the app on the device.</p>
          ) : step.kind === 'wait' ? (
            <div className="flex items-center gap-2">
              <input
                id={inputId}
                type="number"
                min={0}
                max={30000}
                step={100}
                readOnly={readOnly}
                value={step.value}
                onChange={(e) => dispatch({ type: 'update', id: step.id, patch: { value: e.target.value } })}
                className={clsx(fieldBase, 'h-10 w-36')}
              />
              <span className="text-sm text-fg-muted">milliseconds</span>
            </div>
          ) : (
            <textarea
              ref={textRef}
              id={inputId}
              rows={1}
              readOnly={readOnly}
              value={step.value}
              placeholder={meta.examples[step.kind === 'open' ? 1 : 0]}
              onChange={(e) => dispatch({ type: 'update', id: step.id, patch: { value: e.target.value } })}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${el.scrollHeight}px`;
              }}
              className={clsx(fieldBase, 'min-h-10 resize-none py-2.5 leading-relaxed', step.kind === 'open' && 'font-mono text-[13px]')}
              aria-invalid={(showErrors && !!problem) || !!variableMessages?.length}
              aria-describedby={
                [showErrors && problem ? `${inputId}-err` : null, variableMessages?.length ? variableErrId : null].filter(Boolean).join(' ') || undefined
              }
              maxLength={2000}
            />
          )}

          {showErrors && problem && (
            <p id={`${inputId}-err`} className="text-xs text-danger-text">
              {problem}
            </p>
          )}

          {!!variableMessages?.length && (
            <ul id={variableErrId} className="space-y-0.5 text-xs text-danger-text" aria-live="polite">
              {variableMessages.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}

          {showHelp && (
            <div className="animate-fade-in rounded-field bg-primary-subtle/50 p-3 text-xs text-fg-secondary ring-1 ring-primary/30 ring-inset">
              <p>{meta.hint}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {meta.examples.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => dispatch({ type: 'update', id: step.id, patch: { value: ex } })}
                    className="rounded-full bg-surface px-3 py-[5px] text-fg ring-1 ring-border ring-inset transition hover:ring-primary"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {!readOnly && (
          <div className="flex flex-col gap-0.5 opacity-100 transition sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
            <IconBtn label="Move up" disabled={index === 0} onClick={() => dispatch({ type: 'move', id: step.id, direction: -1 })}>
              <ArrowUp className="size-4" />
            </IconBtn>
            <IconBtn label="Move down" disabled={index === total - 1} onClick={() => dispatch({ type: 'move', id: step.id, direction: 1 })}>
              <ArrowDown className="size-4" />
            </IconBtn>
            <IconBtn label="Duplicate step" onClick={() => dispatch({ type: 'duplicate', id: step.id })}>
              <Copy className="size-4" />
            </IconBtn>
            <IconBtn label="Delete step" danger onClick={() => dispatch({ type: 'remove', id: step.id })}>
              <Trash2 className="size-4" />
            </IconBtn>
          </div>
        )}
      </div>
      {dropTarget === 'after' && <div className="absolute right-0 -bottom-2 left-0 h-0.5 rounded-full bg-primary" aria-hidden />}
    </li>
  );
});

function IconBtn({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'grid place-items-center rounded-full text-fg ring-1 ring-border ring-inset transition size-8 disabled:opacity-40',
        danger ? 'not-disabled:hover:bg-danger-subtle not-disabled:hover:text-danger-text not-disabled:hover:ring-danger-line' : 'not-disabled:hover:bg-fg/8',
      )}
    >
      {children}
    </button>
  );
}
